import kanbanAPI from 'dashboard/api/kanban';

export default {
  data() {
    return {
      dropMarker: { columnId: null, index: -1 },
      _dragStartX: null,
      _dragMoveHandler: null,
      _lastCardMoveAt: 0, // anti-duplicação
    };
  },

  methods: {
    // substitui o método atual
    onCardClick(card, evt) {
      try { evt?.stopPropagation?.(); evt?.preventDefault?.(); } catch {}

      // se o componente pai injetou o handler "bom", usa ele
      if (typeof this.onCardClickFn === 'function') {
        return this.onCardClickFn(card, evt);
      }

      // fallback ultra-seguro: NUNCA navegar por conversation_id aqui
      const displayId =
        Number(card?.conversation_display_id) ||
        (typeof card?.id === 'string' && card.id.startsWith('conv-')
          ? Number(card.id.slice(5))
          : null);
      const accountId = Number(this.accountId) || Number(card?.account_id);

      if (displayId && accountId) {
        this.$router.push({
          path: `/app/accounts/${accountId}/conversations/${displayId}`,
        });
      } else {
        // não sabemos o display_id -> melhor não abrir nada errado
        console.warn('KANBAN_NAV: click ignorado — display_id/accountId ausentes', { card });
      }
    },

    /* ===== Config do DnD por coluna ===== */
    // Virtual "Novo": copia ao puxar e NÃO aceita drop para dentro
    cardsDndGroup(col) {
      if (this.isVirtual(col)) {
        return { name: 'kanban-cards', pull: 'clone', put: false };
      }
      return { name: 'kanban-cards', pull: true, put: true };
    },

    // clone usado quando pull='clone'
    cloneCard(card) {
      const copy = JSON.parse(JSON.stringify(card));
      if (copy.persisted == null) copy.persisted = false; // marca como placeholder
      return copy;
    },

    /* ===== Helpers ===== */
    isConversationPlaceholder(card) {
      return (
        typeof card?.id === 'string' || // "conv-42"
        card?.kind === 'conversation' ||
        card?.persisted === false
      );
    },

    // remove da coluna virtual qualquer item com o mesmo conversation_id
    removeFromVirtual(cardOrConvId) {
      const convId =
        typeof cardOrConvId === 'number'
          ? cardOrConvId
          : (cardOrConvId?.conversation_id ??
             (typeof cardOrConvId?.id === 'string' && cardOrConvId.id.startsWith('conv-')
               ? Number(cardOrConvId.id.slice(5))
               : null));
      if (!convId) return;

      const vcol = (this.board?.columns || []).find(c => this.isVirtual(c));
      if (!vcol || !Array.isArray(vcol.cards)) return;

      const idx = vcol.cards.findIndex(c => {
        const cid = c?.conversation_id ??
          (typeof c?.id === 'string' && c.id.startsWith('conv-') ? Number(c.id.slice(5)) : null);
        return cid === convId;
      });
      if (idx >= 0) vcol.cards.splice(idx, 1);
    },

    /* ===== Drop marker visual ===== */
    isDropAt(colId, idx) {
      return this.dropMarker.columnId === colId && this.dropMarker.index === idx;
    },

    /* ===== Tilt (efeito ao arrastar) ===== */
    onDragStart(evt) {
      const oe = evt?.originalEvent;
      this._dragStartX = (oe && 'clientX' in oe) ? oe.clientX : null;
      document.body.classList.add('no-text-select');
      this._dragMoveHandler = e => {
        let x = null;
        if (e?.touches && e.touches[0]) x = e.touches[0].clientX;
        else if ('clientX' in e) x = e.clientX;
        if (x != null) this._applyTiltByClientX(x);
      };
      window.addEventListener('mousemove', this._dragMoveHandler, { passive: true });
      window.addEventListener('touchmove', this._dragMoveHandler, { passive: true });
    },
    _getDragEl() {
      return (
        document.querySelector('.sortable-drag') ||
        document.querySelector('.card-drag') ||
        document.querySelector('.card-chosen')
      );
    },
    _applyTiltByClientX(x) {
      const el = this._getDragEl();
      if (!el) return;
      const start = this._dragStartX ?? x;
      const dx = x - start;
      const maxDeg = 5;
      const sens = 0.05;
      const deg = Math.max(-maxDeg, Math.min(maxDeg, dx * sens));
      el.style.transform = `rotate(${deg}deg) scale(0.98)`;
    },

    /* ===== DnD ===== */
    onCardMove(evt, col) {
      if (evt?.originalEvent && 'clientX' in evt.originalEvent) {
        this._applyTiltByClientX(evt.originalEvent.clientX);
      }

      const rc = evt.relatedContext;
      const listEl = evt.to;
      const lastIdx = (col.cards?.length || 0) - 1;
      let idx;

      if (!rc || typeof rc.index !== 'number') {
        idx = (col.cards?.length || 0);
      } else {
        idx = evt.willInsertAfter ? rc.index + 1 : rc.index;
        if (rc.index === lastIdx && evt.originalEvent) {
          const lastEl = listEl.querySelector('.kanban-card:last-of-type');
          if (lastEl) {
            const rect = lastEl.getBoundingClientRect();
            const pointerY = evt.originalEvent.clientY || 0;
            const passedBottomHalf = pointerY > rect.top + rect.height * 0.6;
            if (passedBottomHalf) idx = lastIdx + 1;
          } else {
            idx = lastIdx + 1;
          }
        }
      }

      const max = (col.cards?.length || 0);
      if (idx < 0) idx = 0;
      if (idx > max) idx = max;

      this.dropMarker = { columnId: col.id, index: idx };
      return true;
    },

    onDragEnd() {
      window.removeEventListener('mousemove', this._dragMoveHandler || (() => {}));
      window.removeEventListener('touchmove', this._dragMoveHandler || (() => {}));
      this._dragMoveHandler = null;

      const el = this._getDragEl();
      if (el) el.style.transform = '';

      document.body.classList.remove('no-text-select');

      this._dragStartX = null;
      this.dropMarker = { columnId: null, index: -1 };
    },

    async onCardChange(evt, col) {
      this.dropMarker = { columnId: null, index: -1 };

      try {
        if (evt.moved) {
          // reordenação / movimento de card persistido
          const now = Date.now();
          if (now - this._lastCardMoveAt < 150) return; // evita chamadas duplicadas
          this._lastCardMoveAt = now;

          const card = evt.moved.element;
          const to = evt.moved.newIndex;
          await kanbanAPI.moveCard(card.id, {
            target_position: to,
          });
        } else if (evt.added) {
          // chegou a esta coluna
          const card = evt.added.element;
          const to = evt.added.newIndex;

          const isConversationCard =
            card?.kind === 'conversation' ||
            (typeof card?.id === 'string' && card.id.startsWith('conv-')) ||
            (!!card?.conversation_id && !card?.persisted);

          if (isConversationCard) {
            // criar card persistido na posição correta
            const payload = {
              column_id: col.id,
              position: to, // importante: position (não target_position)
              title: card.title || `Conversa #${card.conversation_id}`,
              description: card.description || null,
              conversation_id:
                card.conversation_id || Number(String(card.id).replace('conv-', '')),
            };

            const { data: created } = await kanbanAPI.createCard(payload);

            const safeNum = v => {
              const n = Number(v);
              return Number.isFinite(n) ? n : null;
            };
            const displayId = safeNum(card?.conversation_display_id) ?? safeNum(card?.display_id);
            const inboxId   = safeNum(card?.inbox_id ?? card?.inbox?.id ?? card?.meta?.inbox_id);
            const accountId = safeNum(card?.account_id ?? this.accountId);

            // —— hidratado: garante IDs para clique imediato pós-drop ——
            const hydrated = {
              ...created,
              persisted: true,

              // IDs fortes
              conversation_id:
                created?.conversation_id ??
                payload?.conversation_id ??
                safeNum(card?.conversation_id) ??
                (typeof card?.id === 'string' && card.id.startsWith('conv-')
                  ? safeNum(card.id.slice(5))
                  : null),

              ...(displayId ? { conversation_display_id: displayId, display_id: displayId } : {}),
              ...(inboxId   ? { inbox_id: inboxId } : {}),
              ...(accountId ? { account_id: accountId } : {}),

              // Infos visuais (mantém o que já havia)
              title: card.contact_name || created.title || card.title,
              contact_name: card.contact_name || null,
              avatar_url: card.avatar_url || null,
              inbox_name: card.inbox_name || null,
              inbox_description: card.inbox_description || null,
              last_messages: Array.isArray(card.last_messages) ? card.last_messages : [],
            };

            // coloca no destino (substitui o placeholder que o draggable inseriu)
            col.cards.splice(to, 1, hydrated);

            // REMOVE imediatamente da coluna virtual "Novo"
            this.removeFromVirtual(card);
          } else {
            // mover card já persistido entre colunas
            await kanbanAPI.moveCard(card.id, {
              target_column_id: col.id,
              target_position: to,
            });
          }
        }
      } catch (e) {
        this.error = e?.response?.data?.error || e.message || 'Falha ao mover/criar card';
      } finally {
        this.deferUpdateHeights?.();
      }
    },
  },
};
